import React, { useState, useRef, useCallback, useEffect } from 'react';
import { bkkAt, bkkNow, fmtBkk, toBkkInput, fromBkkInput } from '../../utils/bkkTime';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { format } from 'date-fns';
import { api } from '../../utils/api';
import { labelView } from '../../utils/emailLabels';
import Composer from '../Composer';
import { Email } from '../../types';
import AddToLeadModal from '../Leads/AddToLeadModal';
import { ContactForm } from '../Contact';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}

// Also used by the email list's attachment chips (Default density), so both show the same icon.
export function fileIcon(contentType: string): string {
  if (contentType.startsWith('image/')) return 'ti-photo';
  if (contentType.includes('pdf')) return 'ti-file-type-pdf';
  if (contentType.includes('word') || contentType.includes('document')) return 'ti-file-type-doc';
  if (contentType.includes('spreadsheet') || contentType.includes('excel')) return 'ti-file-type-xls';
  if (contentType.includes('zip') || contentType.includes('rar') || contentType.includes('7z')) return 'ti-file-zip';
  return 'ti-file';
}

// The file's bytes, fetched with the login token (a plain link can't carry it) — null if the server
// refuses. Shared by Download and the email list's attachment preview.
async function fetchAttachment(emailId: string, attId: string): Promise<Blob | null> {
  const token = localStorage.getItem('mf_token');
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const res = await fetch(`${apiBase}/emails/${emailId}/attachments/${attId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.ok ? res.blob() : null;
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

export async function downloadAttachment(emailId: string, attId: string, filename: string) {
  const blob = await fetchAttachment(emailId, attId);
  if (blob) saveBlob(blob, filename);
}

// What the attachment preview can draw in the page: PDFs (the browser's own PDF viewer), pictures and
// plain text. Judged by the file's type AND its name — some mailers send a PDF as a generic
// "application/octet-stream". Anything else (Word, Excel, zip…) gets the no-preview card.
type PreviewKind = 'pdf' | 'image' | 'text' | 'none';
function previewKind(type: string, name: string): PreviewKind {
  const ct = (type || '').toLowerCase().split(';')[0].trim();
  const ext = (/\.([a-z0-9]+)$/i.exec(name)?.[1] || '').toLowerCase();
  if (ct.includes('pdf') || ext === 'pdf') return 'pdf';
  if (/^image\/(png|jpe?g|gif|webp|bmp|svg\+xml|avif)$/.test(ct) || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif'].includes(ext)) return 'image';
  if (ct === 'text/plain' || ct === 'text/csv' || ['txt', 'csv', 'log'].includes(ext)) return 'text';
  return 'none';
}

// Translate in the preview (Calem, Bugify #87afb73e): the languages offered, and which drawn files
// Claude can read — every PDF and text file, but of the pictures only JPG / PNG / GIF / WEBP (the
// preview also draws BMP, SVG and AVIF, which get no Translate).
type DocLang = 'th' | 'en' | 'my';
const DOC_LANGS: DocLang[] = ['th', 'en', 'my'];
function canTranslate(kind: PreviewKind, type: string, name: string): boolean {
  if (kind === 'pdf' || kind === 'text') return true;
  if (kind !== 'image') return false;
  const ct = (type || '').toLowerCase().split(';')[0].trim();
  const ext = (/\.([a-z0-9]+)$/i.exec(name)?.[1] || '').toLowerCase();
  return /^image\/(png|jpe?g|gif|webp)$/.test(ct) || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
}

// Attachment preview — opened from an attachment chip in the email list, over the whole page, instead
// of downloading the file (Calem, Bugify #e0e30d4b screenshot-2). File name + size, Download, close ×
// (Esc, or a click on the dark backdrop, also close it). HTML files are never drawn here: the file is
// served from the app's own origin, so only PDF (typed as PDF), <img> and escaped text are used.
// Beside Download: a language and Translate, which shows the translation in place of the file, and
// Original to go back. The server escapes the translation before wrapping it in <p>, so it is drawn
// as HTML here.
export function AttachmentPreview({ emailId, att, onClose }: { emailId: string; att: { id: string; name: string; size: number; type: string }; onClose: () => void }) {
  const { t } = useTranslation();
  const uiLang = useStore(s => s.uiLang);
  const [kind, setKind] = useState<PreviewKind>(() => previewKind(att.type, att.name));
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState('');
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  // The language picked, the translation on screen ('' = the file itself), and each language's
  // result and request in flight — kept for as long as the preview is open.
  const [lang, setLang] = useState<DocLang>(uiLang === 'th' ? 'th' : 'en');
  const [onScreen, setOnScreen] = useState<DocLang | ''>('');
  const [done, setDone] = useState<Record<string, { html: string; truncated: boolean }>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [trError, setTrError] = useState('');
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  useEffect(() => {
    const k = previewKind(att.type, att.name);
    if (k === 'none') return;   // nothing to draw — the file is fetched only if they press Download
    let live = true, made = '';
    fetchAttachment(emailId, att.id).then(async b => {
      if (!live) return;
      if (!b) { setFailed(true); return; }
      setBlob(b);
      if (k === 'text') { const s = await b.text(); if (live) setText(s); return; }
      // The browser's PDF viewer only opens a file marked as PDF; anything else it downloads again.
      made = URL.createObjectURL(k === 'pdf' ? new Blob([b], { type: 'application/pdf' }) : b);
      setUrl(made);
    }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [emailId, att.id, att.type, att.name]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The bytes already fetched for the preview are saved as they are; otherwise they are fetched now.
  const download = () => { if (blob) saveBlob(blob, att.name); else void downloadAttachment(emailId, att.id, att.name); };
  const shown = kind !== 'none' && !failed;
  const translatable = shown && canTranslate(kind, att.type, att.name);
  const langName = (l: DocLang) => t(`eml_att_lang_${l}`);

  // Show language l's translation: at once if it is already here, otherwise a spinner until it comes.
  // A failure puts the file back on screen with the reason in a line under the header.
  const translate = (l: DocLang) => {
    setTrError('');
    setOnScreen(l);
    if (done[l] || busy[l]) return;
    setBusy(b => ({ ...b, [l]: true }));
    api.translateAttachment(emailId, att.id, l)
      .then(r => { if (alive.current) setDone(d => ({ ...d, [l]: { html: r.translated || '', truncated: !!r.truncated } })); })
      .catch((e: any) => {
        if (!alive.current) return;
        setOnScreen(s => (s === l ? '' : s));
        // A proxy's own error page (nginx's 504) is markup, not a reason — req() flags it htmlPage.
        const why = e?.htmlPage ? '' : String(e?.message || '');
        setTrError(t('eml_att_translate_failed', { lang: langName(l) }) + (why ? ` — ${why}` : ''));
      })
      .finally(() => { if (alive.current) setBusy(b => ({ ...b, [l]: false })); });
  };
  // While a translation is on screen, picking another language shows that one; otherwise the pick
  // waits for Translate.
  const pickLang = (l: DocLang) => { setLang(l); if (onScreen) translate(l); };

  const spinner = (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <i className="ti ti-loader-2" style={{ fontSize: 26, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
    </div>
  );
  const downloadBtn = (
    <button onClick={download} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', flexShrink: 0 }}>
      <i className="ti ti-download" style={{ fontSize: 13 }} aria-hidden />{t('eml_att_download_btn')}
    </button>
  );
  // Outlined in the accent beside the filled Download; Translate is washed while its language is on screen.
  const outlineBtn = (on: boolean): React.CSSProperties => ({ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius-md)', border: '1px solid var(--accent)', background: on ? 'var(--accent-bg)' : 'transparent', color: 'var(--accent)', cursor: 'pointer', flexShrink: 0 });
  const translateControls = (
    <>
      <select value={lang} onChange={e => pickLang(e.target.value as DocLang)} title={t('eml_att_translate_to')}
        style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--c-gray-50)', fontSize: 12, color: 'var(--text-primary)', padding: '6px 8px', cursor: 'pointer', outline: 'none', fontWeight: 500, flexShrink: 0 }}>
        {DOC_LANGS.map(l => <option key={l} value={l}>{langName(l)}</option>)}
      </select>
      <button onClick={() => translate(lang)} style={outlineBtn(onScreen === lang)}>
        <i className={`ti ${busy[lang] ? 'ti-loader-2' : 'ti-language'}`} style={{ fontSize: 13, animation: busy[lang] ? 'spin 1s linear infinite' : undefined }} aria-hidden />{t('eml_att_translate_btn')}
      </button>
      {onScreen && (
        <button onClick={() => setOnScreen('')} style={outlineBtn(false)}>
          <i className="ti ti-file-text" style={{ fontSize: 13 }} aria-hidden />{t('eml_att_original_btn')}
        </button>
      )}
    </>
  );
  const shownTr = onScreen ? done[onScreen] : undefined;
  const translation = shownTr ? (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16, background: 'var(--bg-panel)' }}>
      <div lang={onScreen} className="rte-render rte-lg" dangerouslySetInnerHTML={{ __html: shownTr.html }}
        style={{ maxWidth: 820, margin: '0 auto', padding: '26px 32px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }} />
      {shownTr.truncated && (
        <div style={{ maxWidth: 820, margin: '10px auto 0', fontSize: 12, color: 'var(--text-tertiary)' }}>{t('eml_att_translate_cut')}</div>
      )}
    </div>
  ) : (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
      <i className="ti ti-loader-2" style={{ fontSize: 26, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
      {onScreen && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('eml_att_translating', { lang: langName(onScreen) })}</div>}
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: shown ? 'min(1100px, 100%)' : 'min(420px, 100%)', height: shown ? '100%' : undefined, maxHeight: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px 12px 18px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <i className={`ti ${fileIcon(att.type || '')}`} style={{ fontSize: 20, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div title={att.name} style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{att.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{formatBytes(att.size)}</div>
          </div>
          {translatable && translateControls}
          {shown && downloadBtn}
          <button onClick={onClose} title={t('eml_att_close')} style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="ti ti-x" style={{ fontSize: 15 }} aria-hidden />
          </button>
        </div>
        {translatable && trError && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 18px', fontSize: 12, color: '#E24B4A', background: 'color-mix(in srgb, #E24B4A 7%, transparent)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <i className="ti ti-alert-circle" style={{ fontSize: 14, flexShrink: 0 }} aria-hidden />{trError}
          </div>
        )}
        {!shown ? (
          /* Word / Excel / zip…, a picture this browser can't draw, or a file that failed to load */
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '30px 24px 28px', textAlign: 'center' }}>
            <i className={`ti ${failed ? 'ti-file-alert' : fileIcon(att.type || '')}`} style={{ fontSize: 44, color: failed ? '#E24B4A' : 'var(--text-tertiary)' }} aria-hidden />
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>{t(failed ? 'eml_att_load_failed' : 'eml_att_no_preview')}</div>
            {downloadBtn}
          </div>
        ) : translatable && onScreen ? (
          translation
        ) : kind === 'pdf' ? (
          url ? <iframe src={url} title={att.name} style={{ flex: 1, minHeight: 0, width: '100%', border: 'none', background: 'var(--bg-panel)' }} /> : spinner
        ) : kind === 'image' ? (
          url ? (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'auto', padding: 16, background: 'var(--bg-panel)' }}>
              <img src={url} alt={att.name} onError={() => setKind('none')} style={{ maxWidth: '100%', maxHeight: '100%', margin: 'auto', objectFit: 'contain', borderRadius: 4 }} />
            </div>
          ) : spinner
        ) : text !== null ? (
          <pre style={{ flex: 1, minHeight: 0, overflow: 'auto', margin: 0, padding: '14px 18px', fontFamily: 'var(--font-mono)', fontSize: 12.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--text-primary)' }}>{text}</pre>
        ) : spinner}
      </div>
    </div>
  );
}

// The "opened" image a quotation's To: copy carries (Calem, Bugify #bf7d6a54). Our Sent copy has it
// too, so it is taken out before anything is drawn — reading your own email must never count as the
// client opening it. Same pattern as the server's stripOpenPixel (services/peakPublic.ts).
const OPEN_PIXEL = /<img\b[^>]*\/api\/public\/peak\/[^"'\s>/]+\/open\.gif[^>]*>/gi;

export function HtmlFrame({ html: raw }: { html: string }) {
  const html = (raw || '').replace(OPEN_PIXEL, '');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(200);
  const onLoad = useCallback(() => {
    const measure = () => {
      const iframe = iframeRef.current;
      if (!iframe) return;
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc) setHeight(Math.max(80, (doc.documentElement.scrollHeight || doc.body.scrollHeight) + 20));
      } catch {}
    };
    measure();
    // Images/webfonts finish after the load event — re-measure so tall bodies aren't cut off.
    setTimeout(measure, 300);
    setTimeout(measure, 1200);
  }, []);
  // Inject <base target="_blank"> so all links open in new tab, not navigate the app
  const safeHtml = html.includes('<head>')
    ? html.replace('<head>', '<head><base target="_blank" rel="noopener noreferrer">')
    : `<base target="_blank" rel="noopener noreferrer">${html}`;
  return (
    <iframe ref={iframeRef} srcDoc={safeHtml} onLoad={onLoad} title="email-body"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      style={{ border: 'none', width: '100%', height, display: 'block' }} />
  );
}

function ConfirmDialog({ message, onConfirm, onCancel }: { message: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', padding: '24px 28px', width: 340, textAlign: 'center' }}>
        <i className="ti ti-alert-triangle" style={{ fontSize: 32, color: '#E24B4A', marginBottom: 12, display: 'block' }} aria-hidden />
        <p style={{ fontSize: 14, color: 'var(--text-primary)', marginBottom: 20, lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={onCancel} style={{ padding: '8px 20px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ padding: '8px 20px', fontSize: 13, borderRadius: 'var(--radius-md)', border: 'none', background: '#E24B4A', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// Blank room below the end of an open email for the module's floating "+" (52px, 24px in from the
// bottom-right corner), so the last attachments and buttons scroll up clear of it, not beneath it.
const FAB_ROOM = 88;

// Attachments panel — shared between both tabs
function AttachmentsPanel({ emailId, attachments }: { emailId: string; attachments: any[] }) {
  if (attachments.length === 0) return null;
  return (
    <div style={{ margin: '16px 20px 0', paddingTop: 12, borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
        <i className="ti ti-paperclip" style={{ fontSize: 13 }} aria-hidden />
        {attachments.length} attachment{attachments.length > 1 ? 's' : ''}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {attachments.map((att: any) => (
          <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', maxWidth: 240 }}>
            <i className={`ti ${fileIcon(att.type || '')}`} style={{ fontSize: 18, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={att.name}>{att.name}</div>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{formatBytes(att.size)}</div>
            </div>
            <button onClick={() => downloadAttachment(emailId, att.id, att.name)} title="Download" style={{ border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 4, borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <i className="ti ti-download" style={{ fontSize: 14 }} aria-hidden />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// System folder names — the same lookup the email list header uses for its title.
const FOLDER_NAMES: Record<string, string> = {
  inbox: 'Inbox', sent: 'Sent', starred: 'Starred',
  archive: 'Archive', drafts: 'Drafts', trash: 'Trash',
};

// "← Inbox" — split screen off only, where the reading pane replaces the list. Names the folder / label
// the list is showing, since that is where the button goes — the name alone, without "Back to" (Calem,
// Bugify #c1ff895b). Shared by both views.
// Same size as the action-bar buttons beside it (5px 10px, 11.5px text), so the whole row is one height.
// It wears the open mailbox's colour the way the sidebar's Sync bubble does — a pale wash, its words
// in the colour itself and a thin outline of it — through the module's --accent, so it is teal while no
// mailbox is open, like the floating "+". Calem (Bugify #e0e30d4b) asked for it in place of plain grey.
// The wash is half the Sync bubble's: two shades paler, like the rest of the row (Calem, Bugify #c1ff895b).
function BackButton({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  const { activeFolder, activeAccountId, labelOverrides } = useStore();
  const folder = FOLDER_NAMES[activeFolder] || (activeFolder === 'spam' ? t('spam') : activeFolder === 'snoozed' ? t('eml_snoozed') : '') || labelOverrides[activeAccountId || '']?.[activeFolder]?.name || activeFolder || 'Inbox';
  return (
    <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', fontSize: 11.5, fontWeight: 500, borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--accent) 35%, transparent)', background: 'color-mix(in srgb, var(--accent-bg) 50%, transparent)', color: 'var(--accent)', cursor: 'pointer' }}>
      <i className="ti ti-arrow-left" style={{ fontSize: 12 }} aria-hidden />{t('eml_back_to_list', { folder })}
    </button>
  );
}

// ── Sender → Contacts (Calem, Bugify #1b459002) ──────────────────────────────
const addrKey = (a: string) => (a || '').trim().toLowerCase();

// Which of these sender addresses are already on a card in Contacts. /contacts/search matches name OR
// address loosely, and a card can hold several addresses ("a@x.com, b@y.com"), so only the exact
// address on a card counts. An address still being looked up — or whose lookup failed — is absent.
function useSavedContacts(addresses: string[]) {
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const asked = useRef<Set<string>>(new Set());
  const want = Array.from(new Set(addresses.map(addrKey).filter(Boolean))).sort().join('\n');
  useEffect(() => {
    for (const a of want ? want.split('\n') : []) {
      if (asked.current.has(a)) continue;
      asked.current.add(a);
      api.searchContacts(a).then((rows: any) => {
        const yes = (Array.isArray(rows) ? rows : []).some((c: any) => String(c.email || '').split(/[,;]/).some(x => addrKey(x) === a));
        setSaved(s => ({ ...s, [a]: yes }));
      }).catch(() => { asked.current.delete(a); });
    }
  }, [want]);
  const markSaved = useCallback((a: string) => setSaved(s => ({ ...s, [addrKey(a)]: true })), []);
  return { saved, markSaved };
}

// Right after the sender's <address>: a small round bubble that opens a NEW Contacts card already
// holding the sender's name and address — or, once that address is on a card, a green check. Worn
// like the sidebar's Sync bubble (the open mailbox's colour). Nothing shows while the lookup is out.
function ContactBubble({ saved, onAdd }: { saved: boolean | undefined; onAdd: () => void }) {
  const { t } = useTranslation();
  if (saved === undefined) return null;
  const round: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, padding: 0, marginLeft: 6, borderRadius: '50%', verticalAlign: 'middle', flexShrink: 0 };
  if (saved) return (
    <span title={t('eml_contact_exists')} style={{ ...round, border: '1px solid rgba(21,128,61,0.25)', background: '#DCFCE7', color: '#15803D' }}>
      <i className="ti ti-check" style={{ fontSize: 11 }} aria-hidden />
    </span>
  );
  return (
    <button onClick={e => { e.stopPropagation(); onAdd(); }} title={t('eml_contact_add')}
      style={{ ...round, border: '1px solid color-mix(in srgb, var(--accent) 35%, transparent)', background: 'var(--accent-bg)', color: 'var(--accent)', cursor: 'pointer' }}>
      <i className="ti ti-user-plus" style={{ fontSize: 11 }} aria-hidden />
    </button>
  );
}

// ── Snooze (Calem, approved) ─────────────────────────────────────────────────
// Times are Bangkok wall-clock (UTC+7, no daylight saving) whatever zone the computer's clock is in.
// Bangkok-time helpers live in utils/bkkTime.ts (shared with the composers' Schedule send menu).
// Later today 18:00 (only before 18:00) · Tomorrow 08:00 · This weekend Sat 08:00 (Mon–Fri only) ·
// Next week Mon 08:00.
function snoozePresets(): { key: string; at: number }[] {
  const { dow, hour } = bkkNow();
  const out: { key: string; at: number }[] = [];
  if (hour < 18) out.push({ key: 'eml_snooze_later_today', at: bkkAt(0, 18) });
  const tomorrow = bkkAt(1, 8);
  out.push({ key: 'eml_snooze_tomorrow', at: tomorrow });
  // On a Friday "This weekend" (Saturday 08:00) IS tomorrow, and on a Sunday "Next week" (Monday
  // 08:00) is — one entry per moment, never two rows with the same time.
  const weekend = bkkAt(6 - dow, 8);
  if (dow >= 1 && dow <= 5 && weekend !== tomorrow) out.push({ key: 'eml_snooze_weekend', at: weekend });
  const nextWeek = bkkAt((8 - dow) % 7 || 7, 8);
  if (nextWeek !== tomorrow) out.push({ key: 'eml_snooze_next_week', at: nextWeek });
  return out;
}

// "Snooze until…" — sits next to Archive, on Inbox emails of the user's own mailbox only (never a
// shared / role one like office@). Inside Snoozed it also offers Unsnooze. `ids` = the email, or a
// conversation's Inbox messages, snoozed together. `canSet` false = the owner of a mailbox that is
// no longer personal (shared since): only Unsnooze is offered. `align` = which edge the menu hangs
// from when there is room for it either way; both action bars end at the pane's right edge (Calem,
// Bugify #c1ff895b), so there it hangs from the right.
function SnoozeButton({ ids, canSet = true, align = 'right' }: { ids: string[]; canSet?: boolean; align?: 'left' | 'right' }) {
  const { t } = useTranslation();
  const { uiLang, activeFolder, snoozeEmails, showToast } = useStore();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [value, setValue] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // Which edge the 260px menu hangs from is decided when it opens, from where the button really is,
  // so a menu opened near either edge of the window is never cut off.
  const [side, setSide] = useState<'left' | 'right'>(align);
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const toggle = () => {
    const r = boxRef.current?.getBoundingClientRect();
    if (r) setSide(r.left + 266 > window.innerWidth ? 'right' : (r.right - 266 < 0 ? 'left' : align));
    setOpen(v => !v); setCustom(false); setErr(''); setValue(toBkkInput(bkkAt(1, 8)));
  };
  // at = ms, or null to unsnooze.
  const run = async (at: number | null) => {
    if (busy) return;
    if (at !== null && !(at > Date.now())) { setErr(t('eml_snooze_past')); return; }
    setBusy(true);
    const ok = await snoozeEmails(ids, at === null ? null : Math.floor(at / 1000));
    setBusy(false);
    setOpen(false);
    showToast(!ok ? t('eml_snooze_failed') : at === null ? t('eml_snooze_unsnoozed') : t('eml_snooze_done', { time: fmtBkk(at, uiLang) }));
  };
  const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 9px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, color: 'var(--text-primary)', borderRadius: 6, textAlign: 'left' };
  const hover = { onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'var(--bg-active)'), onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'transparent') };

  return (
    <div ref={boxRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button onClick={toggle} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', fontSize: 11.5, borderRadius: 'var(--radius-md)', border: '1px solid rgba(202,138,4,0.3)', background: open ? '#FFF9E3' : '#FFFEF4', color: '#CA8A04', cursor: 'pointer' }}>
        <i className="ti ti-clock-pause" style={{ fontSize: 12 }} aria-hidden />{t('eml_snooze')}
      </button>
      {/* Right-aligned under the button, like the Label menu, so it can't run past the pane's edge. */}
      {open && (
        <div style={{ position: 'absolute', top: '100%', ...(side === 'left' ? { left: 0 } : { right: 0 }), marginTop: 6, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', padding: 6, minWidth: 260, zIndex: 50 }}>
          {canSet && <div style={{ padding: '4px 9px 6px', fontSize: 11.5, fontWeight: 600, color: 'var(--text-tertiary)' }}>{t('eml_snooze_until')}</div>}
          {canSet && snoozePresets().map(p => (
            <button key={p.key} disabled={busy} onClick={() => run(p.at)} style={rowStyle} {...hover}>
              <span style={{ flex: 1 }}>{t(p.key)}</span>
              <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{fmtBkk(p.at, uiLang)}</span>
            </button>
          ))}
          {canSet && <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />}
          {canSet && <button disabled={busy} onClick={() => { setCustom(v => !v); setErr(''); }} style={rowStyle} {...hover}>
            <i className="ti ti-calendar-event" style={{ fontSize: 13, color: 'var(--text-tertiary)', width: 14 }} aria-hidden />
            <span style={{ flex: 1 }}>{t('eml_snooze_custom')}</span>
          </button>}
          {canSet && custom && (
            <div style={{ padding: '4px 9px 6px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <input type="datetime-local" value={value} min={toBkkInput(Date.now())} onChange={e => { setValue(e.target.value); setErr(''); }}
                style={{ padding: '6px 8px', fontSize: 12.5, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1, fontSize: 11, color: err ? '#E24B4A' : 'var(--text-tertiary)' }}>{err || t('eml_snooze_bkk')}</span>
                <button disabled={busy || !value} onClick={() => run(fromBkkInput(value))} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', opacity: busy || !value ? 0.6 : 1 }}>
                  {t('eml_snooze')}
                </button>
              </div>
            </div>
          )}
          {activeFolder === 'snoozed' && (<>
            {canSet && <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />}
            <button disabled={busy} onClick={() => run(null)} style={rowStyle} {...hover}>
              <i className="ti ti-clock-play" style={{ fontSize: 13, color: 'var(--text-tertiary)', width: 14 }} aria-hidden />
              <span style={{ flex: 1 }}>{t('eml_snooze_unsnooze')}</span>
            </button>
          </>)}
        </div>
      )}
    </div>
  );
}

type ComposeMode = 'reply' | 'replyAll' | 'forward' | null;
type ActiveTab = 'original' | 'translation';

export default function EmailDetail({ onBack }: { onBack?: () => void } = {}) {
  const { t } = useTranslation();
  const { uiLang, toggleStar, deleteEmail, archiveEmail, moveToSpam, setLabel, markUnread, getActiveEmail, translateEmail, showToast, setActiveEmail, me, folders, labelOverrides, loadEmails, accounts, activeFolder } = useStore();
  const email = getActiveEmail();
  const canLead = me?.role === 'admin' || !!me?.modules?.includes('leads');

  const [activeTab, setActiveTab] = useState<ActiveTab>('original');
  const [composeMode, setComposeMode] = useState<ComposeMode>(null);
  const [addLead, setAddLead] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [confirm, setConfirm] = useState<{ msg: string; action: () => void } | null>(null);
  // The sender's "Add to contacts" bubble — a single email's here; a conversation's in ThreadView.
  const { saved: savedContacts, markSaved } = useSavedContacts(email && (email.threadCount || 1) <= 1 ? [email.from.email] : []);
  const [addContact, setAddContact] = useState<{ name: string; email: string } | null>(null);

  // Translation state — loaded lazily when user clicks the translation tab
  const [translatedBody, setTranslatedBody] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState(false);

  // Reset state when email changes
  useEffect(() => {
    setActiveTab('original');
    setComposeMode(null);
    setTranslatedBody(null);
    setTranslating(false);
    setTranslateError(false);
    setShowLabels(false);
  }, [email?.id]);

  // Lazy-load translation only when translation tab is first clicked
  const handleTranslationTab = useCallback(() => {
    setActiveTab('translation');
    if (!email) return;
    // Same language — nothing to translate
    if (email.lang === uiLang) return;
    // Already loaded or loading
    if (translatedBody !== null || translating) return;
    // Check in-memory cache first
    const cached = email.bodyTranslated[uiLang];
    // Skip stale/broken cached translations
    if (cached && !cached.includes('[No API key configured]')) { setTranslatedBody(cached); return; }
    // Fetch from server
    setTranslating(true);
    setTranslateError(false);
    translateEmail(email.id, uiLang)
      .then(result => setTranslatedBody(result || ''))
      .catch(() => setTranslateError(true))
      .finally(() => setTranslating(false));
  }, [email, uiLang, translatedBody, translating, translateEmail]);

  // Re-trigger translation if user switches UI language while on translation tab
  useEffect(() => {
    if (activeTab === 'translation' && email && email.lang !== uiLang) {
      setTranslatedBody(null);
      setTranslating(false);
      setTranslateError(false);
      const cached = email.bodyTranslated[uiLang];
      if (cached && !cached.includes('[No API key configured]')) { setTranslatedBody(cached); return; }
      setTranslating(true);
      translateEmail(email.id, uiLang)
        .then(r => setTranslatedBody(r || ''))
        .catch(() => setTranslateError(true))
        .finally(() => setTranslating(false));
    }
  }, [uiLang]);

  if (!email) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-surface)' }}>
        <i className="ti ti-mail-opened" style={{ fontSize: 48, color: 'var(--text-tertiary)', marginBottom: 12 }} aria-hidden />
        <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{t('noEmailSelected')}</div>
      </div>
    );
  }

  // Conversations (2+ messages) render the Gmail-style thread view.
  if ((email.threadCount || 1) > 1) return <ThreadView key={email.threadId || email.id} root={email} onBack={onBack} />;

  const langName = (code: string) => t(`langNames.${code}` as any, { defaultValue: code });
  const canTranslate = email.lang !== uiLang;

  const handleDelete = () => setConfirm({
    msg: email.folder === 'trash'
      ? 'Permanently delete this email? This cannot be undone.'
      : 'Move this email to Trash?',
    action: () => {
      setConfirm(null);
      void deleteEmail(email.id);   // the row leaves at once; a failure brings it back with its own toast
      showToast(email.folder === 'trash' ? 'Email permanently deleted' : 'Moved to Trash');
    },
  });

  const handleArchive = () => setConfirm({
    msg: 'Archive this email? It will be moved out of your inbox.',
    action: async () => {
      setConfirm(null);
      await archiveEmail(email.id);
      showToast('Archived');
    },
  });

  const handleSpam = async () => { await moveToSpam(email.id); showToast('Moved to Spam'); };

  // Custom labels of this email's account (from the synced folder list), with the local
  // rename/colour applied and labels deleted in PandaAI Email left out.
  const acctOverrides = labelOverrides[email.accountId];
  const acctLabels = ((folders as any)[email.accountId] || []).filter((f: any) => f.kind === 'label' && !labelView(f, acctOverrides).hidden);
  const snoozeAcct = accounts.find(a => a.id === email.accountId);
  // Set a snooze: own PERSONAL mailbox only. Unsnooze (inside Snoozed): the mailbox's owner, even if it
  // has since been shared — otherwise a snoozed email stays hidden from everyone until the cron.
  const canSnooze = email.folder === 'inbox' && !!snoozeAcct?.personal;
  const canUnsnooze = activeFolder === 'snoozed' && !!snoozeAcct && !snoozeAcct.shared;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg-surface)' }} key={email.id}>

      {/* ── Header ── */}
      <div style={{ padding: '14px 20px 0', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        {/* Subject + meta row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.4, marginBottom: 8 }}>{email.subject}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent-bg)', color: 'var(--accent-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                {(email.from.name || email.from.email).split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{email.from.name || email.from.email}</div>
                <div style={{ display: 'flex', alignItems: 'center', minHeight: 18, fontSize: 11, color: 'var(--text-tertiary)' }}>
                  &lt;{email.from.email}&gt;
                  <ContactBubble saved={savedContacts[addrKey(email.from.email)]} onAdd={() => setAddContact({ name: email.from.name || '', email: email.from.email })} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {email.lang && (
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 'var(--radius-md)', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 500, border: '1px solid var(--border)' }}>
                    {langName(email.lang)}
                  </span>
                )}
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{format(email.date, 'dd/MM/yyyy · HH:mm')}</span>
                <button onClick={() => toggleStar(email.id)} title={email.starred ? 'Unstar' : 'Star'} style={{ width: 28, height: 28, border: 'none', background: 'transparent', color: email.starred ? '#f5a623' : 'var(--text-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius-sm)' }}>
                  <i className={`ti ti-star${email.starred ? '-filled' : ''}`} style={{ fontSize: 15 }} aria-hidden />
                </button>
                {canLead && (
                  <button onClick={() => setAddLead(true)} title={email.lead ? t('ld_change_or_remove') : t('ld_add_to')} style={{ width: 28, height: 28, border: 'none', background: 'transparent', color: '#6D28D9', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius-sm)' }}>
                    <i className="ti ti-target-arrow" style={{ fontSize: 15 }} aria-hidden />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* To / CC */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingLeft: 42, marginBottom: 10 }}>
          {email.to.length > 0 && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>To: </span><span style={{ color: 'var(--text-secondary)' }}>{email.to.join(', ')}</span></div>}
          {email.cc.length > 0 && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>CC: </span><span style={{ color: 'var(--text-secondary)' }}>{email.cc.join(', ')}</span></div>}
        </div>

        {/* ── Tabs, with the action bar beside them. The tabs sit on the header's bottom line (the -1px margin lets
            the open tab cover it and join the content below); the bar takes the rest of the row, centred on the tabs,
            with its buttons pushed to the row's right end, and wraps its groups onto more lines as the pane narrows,
            each line right-aligned too. This row wraps in reverse — its lines stack from
            the bottom up, so flex-start is the bottom here — so a pane too narrow for even the bar's widest group
            beside the tabs puts the bar on a line of its own just above them, instead of squeezing the tabs or
            cutting buttons off at the pane's edge. ── */}
        <div style={{ display: 'flex', flexWrap: 'wrap-reverse', alignItems: 'flex-start', gap: '10px 12px', marginBottom: -1 }}>
          <div style={{ display: 'flex', gap: 0, flexShrink: 0 }}>
            {/* Original tab */}
            <button
              onClick={() => setActiveTab('original')}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 16px', fontSize: 12, fontWeight: activeTab === 'original' ? 600 : 400,
                border: '1px solid var(--border)', borderBottom: activeTab === 'original' ? '1px solid var(--bg-surface)' : '1px solid var(--border)',
                borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
                background: activeTab === 'original' ? 'var(--bg-surface)' : 'var(--c-gray-50)',
                color: activeTab === 'original' ? 'var(--text-primary)' : 'var(--text-secondary)',
                cursor: 'pointer', marginRight: 2, position: 'relative', zIndex: activeTab === 'original' ? 1 : 0,
              }}
            >
              <i className="ti ti-mail" style={{ fontSize: 13 }} aria-hidden />
              Original
              {email.lang && (
                <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 8, background: 'var(--c-gray-100)', color: 'var(--text-tertiary)', fontWeight: 500 }}>
                  {email.lang.toUpperCase()}
                </span>
              )}
            </button>

            {/* Translation tab */}
            <button
              onClick={handleTranslationTab}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 16px', fontSize: 12, fontWeight: activeTab === 'translation' ? 600 : 400,
                border: '1px solid var(--border)', borderBottom: activeTab === 'translation' ? '1px solid var(--bg-surface)' : '1px solid var(--border)',
                borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
                background: activeTab === 'translation' ? 'var(--bg-surface)' : 'var(--c-gray-50)',
                color: activeTab === 'translation' ? 'var(--accent)' : canTranslate ? 'var(--text-secondary)' : 'var(--text-tertiary)',
                cursor: 'pointer', position: 'relative', zIndex: activeTab === 'translation' ? 1 : 0,
              }}
            >
              <i className="ti ti-language" style={{ fontSize: 13, color: activeTab === 'translation' ? 'var(--accent)' : 'inherit' }} aria-hidden />
              Translation
              <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 8, background: activeTab === 'translation' ? 'var(--accent-bg)' : 'var(--c-gray-100)', color: activeTab === 'translation' ? 'var(--accent-text)' : 'var(--text-tertiary)', fontWeight: 500, border: activeTab === 'translation' ? '1px solid color-mix(in srgb, var(--accent) 20%, transparent)' : 'none' }}>
                {uiLang.toUpperCase()}
              </span>
              {translating && <i className="ti ti-loader-2" style={{ fontSize: 11, color: 'var(--accent)', animation: 'spin 1s linear infinite' }} aria-hidden />}
            </button>
          </div>

          {/* ── Action bar — at the right end of the Original / Translation tab row (Calem, Bugify #c1ff895b screenshot-5;
              screenshot-4 had put it straight after the Translation tab); it used to be its own row at the top of the pane,
              above the subject. In groups: Back to the folder, labelled with the folder's name alone ("Inbox")
              (split screen off only; the detail pane replaces the list instead of sitting beside it) ·
              Unread (it marks the email unread; "Mark" left off the label, Calem, Bugify #c1ff895b) · Delete ·
              Reply / Reply All / Forward · Spam / Archive / Label. While replying only Back stays.
              Each group has its own light tint, in that order: the open mailbox's colour · blue · red · green · yellow —
              two shades paler than it was, with each button's words and icon in the full colour of its own outline
              (Calem, Bugify #c1ff895b). Unread's blue is the top menu's, the Quotation / Invoice / Receipt box
              (TAB_GROUPS.peak #378ADD, tabPalette.ts), on #F7FBFE — as pale as the other groups' washes; it was
              orange #EA580C on #FFFBF6 (Calem, Bugify #7f0bc98f). Unread comes straight after Back (Calem, Bugify
              #e0e30d4b screenshot-3) — it leads the bar when split screen is on and there is no Back — and Delete sits
              between it and Reply, with every button at the smaller 5px 10px / 11.5px size (Calem, Bugify #a375cb1a
              screenshot-4). Every button sits 6px from the next, between groups as within Reply / Reply All / Forward,
              the bar still pushed to the right; the groups were 18px apart (Calem, Bugify #7f0bc98f).
              3px above and below keep a wrapped bar's last line of buttons off the header's bottom line. ── */}
          {(onBack || !composeMode) && (
            <div style={{ flex: 1, alignSelf: 'center', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap', margin: '3px 0' }}>
              {onBack && <BackButton onBack={onBack} />}
              {!composeMode && (<>
                {/* Unread — marks it unread and closes the reading pane, so the list shows it as unread again */}
                <button onClick={() => { markUnread(email.id); showToast('Marked as unread'); setActiveEmail(null); }} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', fontSize: 11.5, borderRadius: 'var(--radius-md)', border: '1px solid rgba(55,138,221,0.3)', background: '#F7FBFE', color: '#378ADD', cursor: 'pointer' }}>
                  <i className="ti ti-mail" style={{ fontSize: 12 }} aria-hidden />{t('eml_mark_unread')}
                </button>
                <button onClick={handleDelete} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', fontSize: 11.5, borderRadius: 'var(--radius-md)', border: '1px solid rgba(226,75,74,0.3)', background: '#FFF9F9', color: '#E24B4A', cursor: 'pointer' }}>
                  <i className="ti ti-trash" style={{ fontSize: 12 }} aria-hidden />Delete
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {([
                    { key: 'reply', icon: 'ti-corner-up-left', label: 'Reply' },
                    { key: 'replyAll', icon: 'ti-corner-up-left-double', label: 'Reply All' },
                    { key: 'forward', icon: 'ti-corner-up-right', label: 'Forward' },
                  ] as const).map(({ key, icon, label }) => (
                    <button key={key} onClick={() => setComposeMode(key)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', fontSize: 11.5, fontWeight: 500, borderRadius: 'var(--radius-md)', border: '1px solid rgba(21,128,61,0.25)', background: '#F6FEF9', color: '#15803D', cursor: 'pointer' }}>
                      <i className={`ti ${icon}`} style={{ fontSize: 12 }} aria-hidden />{label}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button onClick={handleSpam} title="Move to Spam" style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', fontSize: 11.5, borderRadius: 'var(--radius-md)', border: '1px solid rgba(202,138,4,0.3)', background: '#FFFEF4', color: '#CA8A04', cursor: 'pointer' }}>
                    <i className="ti ti-alert-octagon" style={{ fontSize: 12 }} aria-hidden />Spam
                  </button>
                  <button onClick={handleArchive} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', fontSize: 11.5, borderRadius: 'var(--radius-md)', border: '1px solid rgba(202,138,4,0.3)', background: '#FFFEF4', color: '#CA8A04', cursor: 'pointer' }}>
                    <i className="ti ti-archive" style={{ fontSize: 12 }} aria-hidden />Archive
                  </button>
                  {(canSnooze || canUnsnooze) && <SnoozeButton ids={[email.id]} canSet={canSnooze} />}
                  {acctLabels.length > 0 && (
                    <div style={{ position: 'relative' }}>
                      <button onClick={() => setShowLabels(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', fontSize: 11.5, borderRadius: 'var(--radius-md)', border: '1px solid rgba(202,138,4,0.3)', background: showLabels ? '#FFF9E3' : '#FFFEF4', color: '#CA8A04', cursor: 'pointer' }}>
                        <i className="ti ti-tag" style={{ fontSize: 12 }} aria-hidden />Label
                      </button>
                      {/* Right-aligned under the button: Label ends the row, so a left-aligned menu could run past the pane's edge and be cut off. */}
                      {showLabels && (
                        <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', padding: 6, minWidth: 210, maxHeight: 260, overflowY: 'auto', zIndex: 50 }}>
                          {acctLabels.map((l: any) => {
                            const has = email.labels.includes(l.path);
                            const v = labelView(l, acctOverrides);
                            return (
                              <button key={l.path} onClick={() => setLabel(email.id, l.path, !has)} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 9px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, color: 'var(--text-primary)', borderRadius: 6, textAlign: 'left' }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                <i className={`ti ti-${has ? 'check' : 'plus'}`} style={{ fontSize: 13, color: has ? '#15803D' : 'var(--text-tertiary)', width: 14 }} aria-hidden />
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: v.color || undefined }}>{v.name}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>)}
            </div>
          )}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

        {/* Original tab content */}
        {activeTab === 'original' && (
          <>
            <div style={{ padding: '16px 20px 0' }}>
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: '#fff' }}>
                <HtmlFrame html={email.bodyOriginal} />
              </div>
            </div>
            <AttachmentsPanel emailId={email.id} attachments={email.attachments as any[]} />
            <div style={{ height: FAB_ROOM }} />
          </>
        )}

        {/* Translation tab content */}
        {activeTab === 'translation' && (
          <>
            <div style={{ padding: '16px 20px 0' }}>
              {!canTranslate ? (
                /* Same language — no translation needed */
                <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                  <i className="ti ti-language-off" style={{ fontSize: 36, color: 'var(--text-tertiary)', marginBottom: 12, display: 'block' }} aria-hidden />
                  <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 4 }}>No translation needed</div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                    The email is already in <b>{langName(uiLang)}</b> — the same as your selected language.
                  </div>
                </div>
              ) : translating ? (
                /* Loading */
                <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                  <i className="ti ti-loader-2" style={{ fontSize: 36, color: 'var(--accent)', marginBottom: 12, display: 'block', animation: 'spin 1s linear infinite' }} aria-hidden />
                  <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 4 }}>Translating…</div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                    Translating from <b>{langName(email.lang)}</b> to <b>{langName(uiLang)}</b>
                  </div>
                </div>
              ) : translateError ? (
                /* Error */
                <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                  <i className="ti ti-alert-circle" style={{ fontSize: 36, color: '#E24B4A', marginBottom: 12, display: 'block' }} aria-hidden />
                  <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 12 }}>Translation failed</div>
                  <button onClick={handleTranslationTab} style={{ padding: '7px 16px', fontSize: 12, borderRadius: 'var(--radius-md)', border: '1px solid var(--accent)', background: 'transparent', color: 'var(--accent)', cursor: 'pointer', fontWeight: 500 }}>
                    Try again
                  </button>
                </div>
              ) : translatedBody !== null ? (
                /* Translated content */
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8 }}>
                    <i className="ti ti-language" style={{ fontSize: 12, color: 'var(--accent)' }} aria-hidden />
                    <span>Translated from <b style={{ color: 'var(--text-secondary)' }}>{langName(email.lang)}</b> to <b style={{ color: 'var(--accent)' }}>{langName(uiLang)}</b></span>
                  </div>
                  <div style={{ border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: '#fff' }}>
                    <HtmlFrame html={translatedBody} />
                  </div>
                </div>
              ) : null}
            </div>
            <AttachmentsPanel emailId={email.id} attachments={email.attachments as any[]} />
            <div style={{ height: FAB_ROOM }} />
          </>
        )}
      </div>

      {/* ── Compose panel ── */}
      {composeMode && (
        <Composer email={email} mode={composeMode} onClose={() => setComposeMode(null)} />
      )}

      {/* ── Confirm dialog ── */}
      {confirm && <ConfirmDialog message={confirm.msg} onConfirm={confirm.action} onCancel={() => setConfirm(null)} />}
      {addLead && email && <AddToLeadModal email={{ id: email.id, subject: email.subject, fromName: email.from.name || '', fromEmail: email.from.email || '', date: email.date, bodyText: email.bodyOriginal || '' }}
        current={email.lead ? { rowId: email.lead.rowId, id: email.lead.id, name: email.lead.name, company: email.lead.company } : undefined}
        onClose={() => setAddLead(false)} onDone={() => { showToast(t('ld_added')); void loadEmails({ silent: true } as any); }} />}
      {addContact && <ContactForm contact={null} initial={addContact} onClose={() => setAddContact(null)}
        onSaved={() => { if (!savedContacts[addrKey(addContact.email)]) showToast(t('eml_contact_added')); markSaved(addContact.email); }} />}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}


// ── Conversation view (Gmail-style) ──────────────────────────────────────────
// All messages of the thread, oldest→newest, fetched WITH bodies in ONE request — expanding a
// message is pure UI (nothing can hang). Older ones collapse to one-line headers; the newest is
// expanded by default. Messages are marked read as the user expands them, and whatever is still
// unread once they leave the conversation (Back, or opening another email from the list).
function ThreadView({ root, onBack }: { root: Email; onBack?: () => void }) {
  const { t } = useTranslation();
  const { uiLang, deleteEmails, archiveEmail, setActiveEmail, showToast, setStar, translateEmail, me, loadEmails, markUnread, accounts, activeFolder } = useStore();
  // Conversations are most of the inbox, so this is the PRIMARY place staff assign / fix a lead.
  const canLead = me?.role === 'admin' || !!me?.modules?.includes('leads');
  const [addLead, setAddLead] = useState(false);
  const [msgs, setMsgs] = useState<any[]>([]);
  const [loadErr, setLoadErr] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showTrans, setShowTrans] = useState<Set<string>>(new Set());
  const [transBusy, setTransBusy] = useState<Set<string>>(new Set());
  const [transBody, setTransBody] = useState<Record<string, string>>({});
  const [compose, setCompose] = useState<{ email: Email; mode: 'reply' | 'replyAll' | 'forward' } | null>(null);
  const [confirm, setConfirm] = useState<{ msg: string; action: () => void } | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [totalCount, setTotalCount] = useState(0);   // server-side total (endpoint caps the payload)
  // The message the top Reply/Reply All/Forward bar acts on — follows whichever message the
  // user most recently expanded (defaults to the latest).
  const [replyTargetId, setReplyTargetId] = useState<string | null>(null);
  // "Add to contacts" beside the sender in each open message's header. Its form sits at the root, not
  // in the header — a click inside it would reach the header and fold the message away.
  const { saved: savedContacts, markSaved } = useSavedContacts(msgs.filter(m => expanded.has(m.id)).map(m => m.from_email || ''));
  const [addContact, setAddContact] = useState<{ name: string; email: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const lastIdRef = useRef<string>('');
  // Land on the TOP of the latest message (its header), not its bottom — the user reads from
  // the beginning of the newest email.
  const scrollToLatest = useCallback(() => {
    const el = lastIdRef.current ? cardRefs.current[lastIdRef.current] : null;
    if (el) el.scrollIntoView({ block: 'start' });
    else if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, []);

  const msgsRef = useRef<any[]>([]);
  const expandedRef = useRef<Set<string>>(new Set());
  useEffect(() => { expandedRef.current = expanded; }, [expanded]);
  const markedRef = useRef<Set<string>>(new Set());   // ids already marked read (guards double-effects)

  const markExpandedRead = useCallback((m: any) => {
    if (!m.unread || markedRef.current.has(m.id)) return;
    markedRef.current.add(m.id);
    api.patchEmail(m.id, { unread: false })   // best-effort; list reconciles on refresh
      .then(() => useStore.getState().loadLabelUnread()).catch(() => {});
    setMsgs(ms => ms.map(x => x.id === m.id ? { ...x, unread: 0 } : x));
    // Reflect it on the list row's aggregate so the conversation stops showing as unread.
    useStore.setState(s => ({
      emails: s.emails.map(r => {
        if (!(r.threadIds || []).includes(m.id)) return r;
        const left = Math.max(0, (r.threadUnread || 0) - 1);
        return { ...r, threadUnread: left, unread: left > 0 };
      }),
    }));
  }, []);

  // Leaving the conversation marks every message still unread in it read, not just the expanded
  // ones — otherwise its row stays unread (and tinted) back in the list.
  const markAllRead = useCallback(() => { msgsRef.current.forEach(markExpandedRead); }, [markExpandedRead]);
  useEffect(() => () => {
    // Unmounting with a different email open = the user went straight to it from the list. Back
    // marks on its own click; delete / archive clear the open email, so they mark nothing.
    const now = useStore.getState().getActiveEmail();
    if (now && (now.threadId || now.id) !== (root.threadId || root.id)) markAllRead();
  }, []); // eslint-disable-line

  useEffect(() => {
    let alive = true;
    const firstLoad = msgsRef.current.length === 0;
    if (firstLoad) { setLoading(true); setLoadErr(false); }
    api.getThread(root.threadId || root.id, root.accountId).then((d: any) => {
      if (!alive) return;
      const rows = d.emails || [];
      const prevIds = new Set(msgsRef.current.map((x: any) => x.id));
      msgsRef.current = rows;
      setMsgs(rows); setTotalCount(d.total || rows.length); setLoading(false); setLoadErr(false);
      const last = rows[rows.length - 1];
      if (prevIds.size === 0) {
        if (last) { lastIdRef.current = last.id; setExpanded(new Set([last.id])); setReplyTargetId(last.id); if (last.unread) markExpandedRead(last); }
        // Land on the latest message; re-run after the body iframes finish measuring their height.
        setTimeout(scrollToLatest, 50); setTimeout(scrollToLatest, 400); setTimeout(scrollToLatest, 1300);
      } else {
        // Background refetch (e.g. a new reply arrived): keep whatever the user expanded,
        // and additionally expand a genuinely new latest message.
        const keep = new Set(Array.from(expandedRef.current).filter(id => rows.some((r: any) => r.id === id)));
        if (last && !prevIds.has(last.id)) { keep.add(last.id); setReplyTargetId(last.id); if (last.unread) markExpandedRead(last); }
        setExpanded(keep);
      }
    }).catch(() => { if (alive && firstLoad) { setLoading(false); setLoadErr(true); } });
    return () => { alive = false; };
  }, [root.threadId, root.id, reloadTick]); // eslint-disable-line

  const toggle = (m: any) => {
    const opening = !expandedRef.current.has(m.id);
    if (opening) markExpandedRead(m);
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(m.id)) n.delete(m.id); else n.add(m.id);
      return n;
    });
    if (opening) {
      setReplyTargetId(m.id);
      const into = () => cardRefs.current[m.id]?.scrollIntoView({ block: 'nearest' });
      setTimeout(into, 30); setTimeout(into, 400);   // again once the body iframe has measured
    } else if (replyTargetId === m.id) {
      const lastMsg = msgsRef.current[msgsRef.current.length - 1];
      setReplyTargetId(lastMsg ? lastMsg.id : null);
    }
  };

  // Build the full Email object for the composer / translation from a thread row.
  const toEmail = (m: any): Email => ({
    id: m.id, accountId: m.account_id, subject: m.subject,
    from: { name: m.from_name, email: m.from_email },
    to: JSON.parse(m.to_list || '[]'), cc: JSON.parse(m.cc_list || '[]'), bcc: [],
    date: new Date(m.date * 1000), lang: m.detected_lang,
    bodyOriginal: m.body_html || '', bodyTranslated: JSON.parse(m.translations || '{}'),
    attachments: JSON.parse(m.attachments || '[]'), unread: false, starred: Boolean(m.starred),
    folder: m.folder, labels: m.labels ? JSON.parse(m.labels) : [],
  });

  const toggleTranslate = async (m: any) => {
    const id = m.id;
    if (showTrans.has(id)) { setShowTrans(prev => { const n = new Set(prev); n.delete(id); return n; }); return; }
    if (!transBody[id]) {
      const cached = (() => { try { return JSON.parse(m.translations || '{}')[uiLang]; } catch { return ''; } })();
      if (cached && !cached.includes('[No API key configured]')) setTransBody(t => ({ ...t, [id]: cached }));
      else {
        setTransBusy(prev => new Set(prev).add(id));
        try {
          const tr = await translateEmail(id, uiLang);
          setTransBody(t => ({ ...t, [id]: tr || '' }));
        } catch { showToast('Translation failed'); setTransBusy(prev => { const n = new Set(prev); n.delete(id); return n; }); return; }
        setTransBusy(prev => { const n = new Set(prev); n.delete(id); return n; });
      }
    }
    setShowTrans(prev => new Set(prev).add(id));
  };

  const delThread = () => setConfirm({
    msg: `Delete this conversation (${msgs.length} emails)?`,
    action: () => {
      setConfirm(null);
      const ids = msgs.length ? msgs.map(m => m.id) : (root.threadIds || [root.id]);
      void deleteEmails(ids);   // the row leaves at once; a failure brings it back with its own toast
      setActiveEmail(null);
      showToast('Conversation moved to Trash');
    },
  });
  const archThread = () => setConfirm({
    msg: `Archive this conversation (${msgs.length} emails)?`,
    action: () => {
      setConfirm(null);
      const ids = msgs.length ? msgs.map(m => m.id) : (root.threadIds || [root.id]);
      for (const id of ids) archiveEmail(id);
      setActiveEmail(null);
      showToast('Conversation archived');
    },
  });

  const initialsOf = (name: string, em: string) => (name || em || '?').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();
  const last = msgs[msgs.length - 1];
  // Whichever message is expanded drives the Reply/Reply All/Forward buttons in the top bar.
  const replyTarget = msgs.find(m => m.id === replyTargetId) || last;
  const replyTargetName = replyTarget ? (replyTarget.from_name || replyTarget.from_email || '') : '';
  // Snooze takes the conversation's Inbox messages together — own (personal) mailbox only.
  const snoozeAcct = accounts.find(a => a.id === root.accountId);
  const canSetSnooze = !!snoozeAcct?.personal;
  const snoozeIds = (canSetSnooze || (activeFolder === 'snoozed' && !!snoozeAcct && !snoozeAcct.shared)) ? msgs.filter(m => m.folder === 'inbox').map(m => m.id) : [];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg-surface)' }}>
      {/* Header — tinted like a single email: one row of grouped actions (Back to the folder, split screen off
          only · Unread · Delete · Reply / Reply All / Forward · Archive), then subject + count. A single email's bar
          sits beside its Original / Translation tabs (Calem, Bugify #c1ff895b); a conversation has no tabs, so its
          bar stays on top. Both are pushed to the right end of their row, wrapped lines too (Calem, Bugify #c1ff895b
          screenshot-5), every button 6px from the next (Calem, Bugify #7f0bc98f); here the reply target fills the
          free space to the buttons' left. */}
      <div style={{ padding: '14px 20px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}>
          {/* The reply target — whose message the Reply / Reply All / Forward here answer */}
          <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {!compose && replyTarget ? `→ ${replyTargetName} · ${format(new Date(replyTarget.date * 1000), 'dd/MM · HH:mm')}` : ''}
          </span>
          {onBack && <BackButton onBack={() => { markAllRead(); onBack?.(); }} />}
          {/* Unread — the newest message goes back to unread and the conversation closes. It clears
              the open email instead of going Back, so the leave-the-thread cleanup doesn't re-read it. */}
          {!compose && (
            <button onClick={() => { markUnread(last?.id || root.id); showToast('Marked as unread'); setActiveEmail(null); }} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', fontSize: 11.5, borderRadius: 'var(--radius-md)', border: '1px solid rgba(55,138,221,0.3)', background: '#F7FBFE', color: '#378ADD', cursor: 'pointer', flexShrink: 0 }}>
              <i className="ti ti-mail" style={{ fontSize: 12 }} aria-hidden />{t('eml_mark_unread')}
            </button>
          )}
          <button onClick={delThread} title="Delete conversation" style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', fontSize: 11.5, borderRadius: 'var(--radius-md)', border: '1px solid rgba(226,75,74,0.3)', background: '#FFF9F9', color: '#E24B4A', cursor: 'pointer', flexShrink: 0 }}>
            <i className="ti ti-trash" style={{ fontSize: 12 }} aria-hidden />Delete
          </button>
          {!compose && replyTarget && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {([['reply', 'ti-corner-up-left', 'Reply'], ['replyAll', 'ti-corner-up-left-double', 'Reply All'], ['forward', 'ti-corner-up-right', 'Forward']] as const).map(([mode, icon, label]) => (
                <button key={mode} onClick={() => setCompose({ email: toEmail(replyTarget), mode: mode as any })} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', fontSize: 11.5, fontWeight: 500, borderRadius: 'var(--radius-md)', border: '1px solid rgba(21,128,61,0.25)', background: '#F6FEF9', color: '#15803D', cursor: 'pointer' }}>
                  <i className={`ti ${icon}`} style={{ fontSize: 12 }} aria-hidden />{label}
                </button>
              ))}
            </div>
          )}
          <button onClick={archThread} title="Archive conversation" style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', fontSize: 11.5, borderRadius: 'var(--radius-md)', border: '1px solid rgba(202,138,4,0.3)', background: '#FFFEF4', color: '#CA8A04', cursor: 'pointer', flexShrink: 0 }}>
            <i className="ti ti-archive" style={{ fontSize: 12 }} aria-hidden />Archive
          </button>
          {snoozeIds.length > 0 && <SnoozeButton ids={snoozeIds} canSet={canSetSnooze} />}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <h2 style={{ flex: 1, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.4, minWidth: 0 }}>
            {root.subject} <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-tertiary)' }}>({totalCount || msgs.length || root.threadCount})</span>
          </h2>
          {canLead && (
            <button onClick={() => setAddLead(true)} title={root.lead ? t('ld_change_or_remove') : t('ld_add_to')}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 11px', fontSize: 12, borderRadius: 'var(--radius-md)', border: `1px solid ${root.lead ? '#6D28D9' : 'var(--border)'}`, background: root.lead ? '#F5F3FF' : 'transparent', color: '#6D28D9', cursor: 'pointer', flexShrink: 0, maxWidth: 190 }}>
              <i className="ti ti-target-arrow" style={{ fontSize: 13, flexShrink: 0 }} aria-hidden />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{root.lead ? (root.lead.name || root.lead.company || 'Lead') : t('ld_add_to')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Messages, oldest → newest */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: `10px 14px ${FAB_ROOM}px`, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {!loading && !loadErr && totalCount > msgs.length && (
          <div style={{ flexShrink: 0, padding: '7px 12px', fontSize: 11.5, color: 'var(--text-tertiary)', background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 10, textAlign: 'center' }}>
            Showing the latest {msgs.length} of {totalCount} messages
          </div>
        )}
        {loading && (
          <div style={{ flexShrink: 0, padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}>
            <i className="ti ti-loader-2" style={{ fontSize: 22, animation: 'spin 1s linear infinite', display: 'inline-block' }} aria-hidden />
          </div>
        )}
        {loadErr && !loading && (
          <div style={{ flexShrink: 0, padding: 32, textAlign: 'center' }}>
            <i className="ti ti-alert-circle" style={{ fontSize: 30, color: '#E24B4A', display: 'block', marginBottom: 10 }} aria-hidden />
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>Failed to load the conversation.</div>
            <button onClick={() => setReloadTick(v => v + 1)} style={{ padding: '7px 16px', fontSize: 12, borderRadius: 'var(--radius-md)', border: '1px solid var(--accent)', background: 'transparent', color: 'var(--accent)', cursor: 'pointer', fontWeight: 500 }}>Try again</button>
          </div>
        )}
        {!loading && !loadErr && msgs.map(m => {
          const open = expanded.has(m.id);
          const name = m.from_name || m.from_email || '?';
          const dt = new Date(m.date * 1000);
          if (!open) {
            const un = Boolean(m.unread);   // unread rows render inverted (dark bg, light text)
            return (
              <button key={m.id} onClick={() => toggle(m)} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '9px 12px', borderRadius: 10, border: un ? '1px solid var(--accent)' : '1px solid var(--border)', background: un ? 'var(--accent)' : 'var(--c-gray-50)', cursor: 'pointer' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: un ? 'rgba(255,255,255,0.22)' : 'var(--accent-bg)', color: un ? '#fff' : 'var(--accent-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{initialsOf(m.from_name, m.from_email)}</div>
                <span style={{ fontSize: 12.5, fontWeight: un ? 700 : 600, color: un ? '#fff' : 'var(--text-primary)', flexShrink: 0, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                <span style={{ flex: 1, fontSize: 12, color: un ? 'rgba(255,255,255,0.85)' : 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.preview || ''}</span>
                {un && <span style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--accent)', background: '#fff', borderRadius: 8, padding: '2px 7px', flexShrink: 0, letterSpacing: 0.3 }}>NEW</span>}
                <span style={{ fontSize: 10.5, color: un ? 'rgba(255,255,255,0.85)' : 'var(--text-tertiary)', flexShrink: 0 }}>{format(dt, 'dd/MM · HH:mm')}</span>
              </button>
            );
          }
          const to = (() => { try { return JSON.parse(m.to_list || '[]'); } catch { return []; } })();
          const cc = (() => { try { return JSON.parse(m.cc_list || '[]'); } catch { return []; } })();
          const atts = (() => { try { return JSON.parse(m.attachments || '[]'); } catch { return []; } })();
          const rawBody = m.body_html || (m.body_text
            ? `<pre style="white-space:pre-wrap;font-family:inherit;margin:0">${String(m.body_text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`
            : '<p style="color:#888">(no content)</p>');
          const body = showTrans.has(m.id) ? (transBody[m.id] || rawBody) : rawBody;
          return (
            <div key={m.id} ref={el => { cardRefs.current[m.id] = el; }} style={{ flexShrink: 0, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)', overflow: 'hidden' }}>
              <div onClick={() => toggle(m)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--accent-bg)', color: 'var(--accent-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 700, flexShrink: 0 }}>{initialsOf(m.from_name, m.from_email)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>{name} <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>&lt;{m.from_email}&gt;</span>
                    <ContactBubble saved={savedContacts[addrKey(m.from_email)]} onAdd={() => setAddContact({ name: m.from_name || '', email: m.from_email })} />
                  </div>
                  {to.length > 0 && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>To: {to.join(', ')}{cc.length ? ` · CC: ${cc.join(', ')}` : ''}</div>}
                </div>
                {m.detected_lang && m.detected_lang !== uiLang && (
                  <button onClick={e => { e.stopPropagation(); toggleTranslate(m); }} title={showTrans.has(m.id) ? 'Show original' : 'Translate'}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', fontSize: 11, borderRadius: 8, border: '1px solid var(--border)', background: showTrans.has(m.id) ? 'var(--bg-active)' : 'transparent', color: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }}>
                    <i className={transBusy.has(m.id) ? 'ti ti-loader-2' : 'ti ti-language'} style={{ fontSize: 12, ...(transBusy.has(m.id) ? { animation: 'spin 1s linear infinite' } : {}) }} aria-hidden />
                    {showTrans.has(m.id) ? 'Original' : 'Translate'}
                  </button>
                )}
                {([['reply', 'ti-corner-up-left', 'Reply'], ['replyAll', 'ti-corner-up-left-double', 'Reply All'], ['forward', 'ti-corner-up-right', 'Forward']] as const).map(([mode, icon, title]) => (
                  <button key={mode} onClick={e => { e.stopPropagation(); setCompose({ email: toEmail(m), mode: mode as any }); }} title={`${title}: ${name}`}
                    style={{ border: 'none', background: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', flexShrink: 0, padding: 2, display: 'flex' }}>
                    <i className={`ti ${icon}`} style={{ fontSize: 14 }} aria-hidden />
                  </button>
                ))}
                <button onClick={e => { e.stopPropagation(); const on = !m.starred; setStar(m.id, on); setMsgs(ms => ms.map(x => x.id === m.id ? { ...x, starred: on ? 1 : 0 } : x)); }}
                  title="Star" style={{ border: 'none', background: 'none', color: m.starred ? '#F5A623' : 'var(--text-tertiary)', cursor: 'pointer', flexShrink: 0 }}>
                  <i className={`ti ti-star${m.starred ? '-filled' : ''}`} style={{ fontSize: 14 }} aria-hidden />
                </button>
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0 }}>{format(dt, 'dd/MM/yyyy · HH:mm')}</span>
              </div>
              <div style={{ padding: '12px 12px 0' }}>
                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: '#fff' }}>
                  <HtmlFrame html={body} />
                </div>
              </div>
              <AttachmentsPanel emailId={m.id} attachments={atts as any[]} />
              <div style={{ display: 'flex', gap: 6, padding: '10px 12px' }}>
                {([['reply', 'ti-corner-up-left', 'Reply'], ['replyAll', 'ti-corner-up-left-double', 'Reply All'], ['forward', 'ti-corner-up-right', 'Forward']] as const).map(([mode, icon, label]) => (
                  <button key={mode} onClick={() => setCompose({ email: toEmail(m), mode: mode as any })} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', fontSize: 11.5, fontWeight: 500, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer' }}>
                    <i className={`ti ${icon}`} style={{ fontSize: 12 }} aria-hidden />{label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {compose && <Composer email={compose.email} mode={compose.mode} onClose={() => setCompose(null)} />}
      {addLead && last && <AddToLeadModal
        email={{ id: last.id, subject: root.subject, fromName: last.from?.name || '', fromEmail: last.from?.email || '', date: last.date, bodyText: String(last.bodyOriginal || '').replace(/<[^>]+>/g, ' ').slice(0, 4000) }}
        current={root.lead ? { rowId: root.lead.rowId, id: root.lead.id, name: root.lead.name, company: root.lead.company } : undefined}
        onClose={() => setAddLead(false)} onDone={() => { showToast(t('ld_added')); void loadEmails({ silent: true } as any); }} />}
      {addContact && <ContactForm contact={null} initial={addContact} onClose={() => setAddContact(null)}
        onSaved={() => { if (!savedContacts[addrKey(addContact.email)]) showToast(t('eml_contact_added')); markSaved(addContact.email); }} />}
      {confirm && <ConfirmDialog message={confirm.msg} onConfirm={confirm.action} onCancel={() => setConfirm(null)} />}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
